import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import ItemListItem from '../ItemListItem';
import { Text } from '../../atoms/typography';
import Checkbox from '../../atoms/Checkbox';
import { styled } from '@mui/system';
import { GroupedVirtuoso } from 'react-virtuoso';
const StyledCheckbox = styled(Checkbox) `
  margin-bottom: 16px;
`;
const ItemList = ({ items, additionalItems, filterText, canAutoSelect = false, showAdditionalItems = false, onToggleAdditionalItems, emptyMessage, isLoading = false, }) => {
    const [filteredItems, setFilteredItems] = useState([]);
    const [filteredAdditionalItems, setFilteredAdditionalItems] = useState([]);
    const [isShowingAdditionalItems, setIsShowingAdditionalItems] = useState(showAdditionalItems);
    const hasAutoSelected = React.useRef(false);
    /**
     * This useEffect handles the following:
     * 1. Formats the provided items to the format required by the ItemListItem component.
     * 2. When a list is filtered, CAN auto select, and there is only one item available, it will
     * automatically select that item.
     */
    useEffect(() => {
        const newFilteredItems = items.filter((item) => {
            return !filterText ||
                item.searchText?.toLowerCase().includes(filterText.toLowerCase());
        });
        setFilteredItems(newFilteredItems);
        let newFilteredAdditionalItems = [];
        if (Array.isArray(additionalItems)) {
            newFilteredAdditionalItems = additionalItems?.filter((item) => {
                return !filterText ||
                    item.searchText?.toLowerCase().includes(filterText.toLowerCase());
            });
            setFilteredAdditionalItems(newFilteredAdditionalItems);
        }
        const autoselectableItem = newFilteredItems[0];
        if (autoselectableItem &&
            filterText &&
            canAutoSelect &&
            newFilteredItems.length === 1 &&
            (!showAdditionalItems || newFilteredAdditionalItems.length === 0) && !hasAutoSelected.current) {
            hasAutoSelected.current = true;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            (async () => {
                await newFilteredItems[0]?.onSelect?.();
            })();
        }
        if (!filterText && hasAutoSelected.current) {
            hasAutoSelected.current = false;
        }
    }, [
        filterText,
        items,
        additionalItems,
        canAutoSelect,
    ]);
    const handleToggleAdditionalItems = () => {
        onToggleAdditionalItems?.(!isShowingAdditionalItems);
        setIsShowingAdditionalItems(!isShowingAdditionalItems);
    };
    const renderItemListItem = (item) => {
        return (_jsx(ItemListItem, { title: item.title, display: item.display, imageUrl: item.imageUrl, onSelect: item.onSelect, buttonLabel: item.buttonLabel, buttonVariant: item.buttonVariant, buttonIcon: item.buttonIcon, onItemCheckedChange: item.onItemCheckedChange, checkboxLabel: item.checkboxLabel, checkboxName: item.checkboxName, isCheckDisabled: item.isCheckDisabled, isLoading: isLoading, alertInfo: item.alertInfo }, item.key));
    };
    const renderList = (items, additionalItems = []) => {
        const allItems = [...items];
        const groupCounts = [items.length];
        if (additionalItems.length) {
            groupCounts[1] = 0;
        }
        if (isShowingAdditionalItems) {
            allItems.push(...additionalItems);
            groupCounts[1] = additionalItems.length;
        }
        const getItemContent = (index) => {
            const item = allItems?.[index];
            if (!item) {
                console.warn('No item found to display in item list');
                // Return an invisible placeholder instead of null to avoid the error:
                // "react-virtuoso: Zero-sized element, this should not happen"
                return _jsx("div", { style: { height: '1px', visibility: 'hidden' } });
            }
            return renderItemListItem(item);
        };
        return (_jsxs(Grid, { item: true, container: true, flexDirection: 'column', width: '100%', height: '100%', children: [!items.length && _jsx(Grid, { item: true, container: true, flex: 0, children: emptyMessage }), _jsx(Grid, { item: true, container: true, flex: 1, children: _jsx(GroupedVirtuoso, { style: { height: '100%', width: '100%' }, overscan: 1500, itemContent: getItemContent, groupCounts: groupCounts, groupContent: (index) => {
                            if (index === 0) {
                                // Return an invisible placeholder instead of null to avoid the error:
                                // "react-virtuoso: Zero-sized element, this should not happen"
                                return _jsx("div", { style: { height: '1px', visibility: 'hidden' } });
                            }
                            return (_jsx(Box, { style: {
                                    backgroundColor: 'white',
                                    padding: '8px',
                                }, children: _jsx(StyledCheckbox, { name: "show-more-items", label: `Show ${filteredAdditionalItems.length} more item(s) from the original order`, checked: isShowingAdditionalItems, onChange: handleToggleAdditionalItems }) }));
                        } }) })] }));
    };
    return (_jsxs(Grid, { item: true, container: true, direction: "column", justifyContent: "center", alignItems: "center", flex: 1, children: [_jsx(Grid, { item: true, container: true, flex: 0, children: filterText && (_jsx(Text, { color: "text.secondary", mb: 1, justifySelf: "center", children: `${filteredItems.length}
              result${filteredItems.length === 1 ? '' : 's'}
              for "${filterText}"` })) }), _jsx(Grid, { item: true, container: true, flex: 1, children: renderList(filteredItems, filteredAdditionalItems) })] }));
};
export default ItemList;
//# sourceMappingURL=index.js.map